import React from "react";
import { motion } from "motion/react";
import { Clock, Camera, Map, Phone, CheckCircle } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { passeios, WHATSAPP_LINK } from "../data/mock";

const PasseiosSection = () => {
  const getWhatsAppUrl = (passeioName) => {
    const message = `Olá! Tenho interesse no passeio ${passeioName}. Gostaria de mais informações.`;
    return `${WHATSAPP_LINK}?text=${encodeURIComponent(message)}`;
  };

  return (
    <section id="passeios" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-[hsl(var(--coral))] text-sm font-semibold uppercase tracking-wider mb-4">
            Pontos Turísticos
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Passeios Inesquecíveis
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Conheça os cartões postais e os lugares mais incríveis do Rio de Janeiro
            com guias especializados e roteiros exclusivos.
          </p>
        </motion.div>

        {/* Passeios Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {passeios.map((passeio, index) => (
            <motion.div
              key={passeio.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
            >
              <Card className="group h-full overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300">
                <div className="relative overflow-hidden">
                  <img
                    src={passeio.image}
                    alt={passeio.name}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute top-4 right-4">
                    <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-gray-800 text-xs font-medium px-3 py-1 rounded-full">
                      <Clock className="w-3 h-3" />
                      {passeio.duration}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="font-display text-lg font-semibold text-white">
                      {passeio.name}
                    </h3>
                  </div>
                </div>
                <CardContent className="p-5">
                  <p className="text-gray-600 text-sm mb-4">
                    {passeio.description}
                  </p>
                  <div className="space-y-2 mb-5">
                    {passeio.includes.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-[hsl(var(--whatsapp))]" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                  <motion.a
                    href={getWhatsAppUrl(passeio.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button className="w-full bg-[hsl(var(--whatsapp))] hover:bg-[hsl(142_69%_38%)] text-white rounded-full py-2.5 h-auto font-medium gap-2 text-sm">
                      <Phone className="w-4 h-4" />
                      Reservar
                    </Button>
                  </motion.a>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PasseiosSection;
